import * as React from "react";

import { Box, Divider, Step, StepLabel, Stepper, Typography } from "@mui/material";

import CustomButton from "../input/CustomButton";

import { scrollToTop } from "../../helper-functions/shared/pageUtils";

/**
 * A custom stepper component.
 *
 * The steps are defined in the 'steps' array, alongside the components.
 * The final step is defined in the 'final' object.
 */
export default function CustomStepper(props) {
    const {
        steps = [], final = {}, callback = () => {
        }
    } = props;

    const [stepIndex, setStepIndex] = React.useState(0);
    const [skipped, setSkipped] = React.useState(new Set());

    const filteredSteps = steps.filter((step) => step?.visible !== false);

    // Scroll to the top of the dialog when the active step changes.
    React.useEffect(() => {
        scrollToTop(getDialogElement());
    }, [stepIndex]);

    /**
     * Return a component for the currently active step.
     */
    const createActiveStepComponent = () => {
        const step = filteredSteps[stepIndex];
        if (!step) return;

        return (
            <>
                {step.component}

                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                    {stepIndex === 0 &&
                        <CustomButton title="Cancel" variant="text" color="error" onClick={() => handleClose(false)}/>}
                    {stepIndex > 0 &&
                        <CustomButton title="Back" variant="text" color="secondary" disabled={stepIndex === 0}
                                      onClick={handleBack}/>}

                    <Box sx={{ flex: "1 1 auto" }}/>

                    {isStepOptional(step) && <CustomButton title="Skip" variant="text" onClick={handleSkip}/>}
                    {isStepCustomButton(step) && step.customButton}

                    <CustomButton
                        title={stepIndex === filteredSteps.length - 1 ? "Finish" : "Next"}
                        disabled={step?.nextDisabled}
                        variant="text"
                        color="primary"
                        onClick={handleNext}
                    />
                </Box>
            </>
        );
    };

    /**
     * Return a component for the finished step.
     */
    const createFinishedComponent = () => {
        const step = final;

        return (
            <>
                {step.component}

                <Box sx={{ display: "flex", flexDirection: "row", pt: 2 }}>
                    <Box sx={{ flex: "1 1 auto" }}/>

                    <CustomButton title="Reset" variant="text" color="error" onClick={handleReset}/>
                    <CustomButton title="Apply" variant="text" color="primary" onClick={() => handleClose(true)}/>
                </Box>
            </>
        );
    };

    // Stepper logic below.
    const isStepOptional = (step) => {
        return step?.optional;
    };

    const isStepCustomButton = (step) => {
        return step?.customButton;
    };

    const isStepSkipped = (step) => {
        return skipped.has(step);
    };

    const getDialogElement = () => {
        return document.getElementsByClassName("MuiDialogContent-root")?.[0];
    };

    const handleNext = () => {
        let newSkipped = skipped;

        if (isStepSkipped(stepIndex)) {
            newSkipped = new Set(newSkipped.values());
            newSkipped.delete(stepIndex);
        }

        setStepIndex((prevActiveStep) => prevActiveStep + 1);
        setSkipped(newSkipped);
    };

    const handleBack = () => {
        setStepIndex((prevActiveStep) => prevActiveStep - 1);
    };

    const handleSkip = () => {
        setStepIndex((prevActiveStep) => prevActiveStep + 1);
        setSkipped((prevSkipped) => {
            const newSkipped = new Set(prevSkipped.values());
            newSkipped.add(stepIndex);

            return newSkipped;
        });
    };

    const handleReset = () => {
        setStepIndex(0);
    };

    const handleClose = (confirm) => {
        callback(confirm);
    };

    return (
        <Box sx={{ width: "100%" }}>
            <Stepper activeStep={stepIndex} alternativeLabel>
                {filteredSteps.map((step, index) => {
                    const stepProps = {};
                    const labelProps = {};

                    if (isStepOptional(step)) labelProps.optional = <Typography variant="caption">Optional</Typography>;
                    if (isStepSkipped(index)) stepProps.completed = false;

                    return (
                        <Step key={step.label} {...stepProps}>
                            <StepLabel {...labelProps}>{step.label}</StepLabel>
                        </Step>
                    );
                })}
            </Stepper>

            <Divider className="sm-margin"/>

            {stepIndex === filteredSteps.length ? createFinishedComponent() : createActiveStepComponent()}
        </Box>
    );
}
